import React, { useState } from 'react';
import axios from 'axios';
import { SqsPageHeader, SqsButton } from './sqs/SqsShell';
import { Megaphone, Tag, Gift, Users, ShoppingBag, Bell, BarChart3 } from 'lucide-react';
import SetupModal from './sqs/SetupModal';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const TOOLS = [
  {
    id: 'promotions', icon: Megaphone, title: 'Promotions',
    desc: 'Run sales, banner promos, and limited-time offers across your site.',
    setup: {
      title: 'Set up a promotion',
      description: 'Create a site-wide promo banner with a discount code.',
      fields: [
        { key: 'promo_name', label: 'Promo name', placeholder: 'Spring Sale', required: true },
        { key: 'discount_code', label: 'Discount code', placeholder: 'SPRING20', required: true },
        { key: 'percent_off', label: 'Percent off (%)', type: 'number', placeholder: '20', required: true },
        { key: 'banner_text', label: 'Banner text', placeholder: 'Use code SPRING20 for 20% off' },
      ],
    },
  },
  {
    id: 'discounts', icon: Tag, title: 'Discount codes',
    desc: 'Create one-off and recurring discount codes to share with customers.',
    setup: {
      title: 'Create a discount code',
      fields: [
        { key: 'code', label: 'Code', placeholder: 'SAVE10', required: true },
        { key: 'type', label: 'Type', type: 'select', options: ['Percent off', 'Fixed amount', 'Free shipping'], required: true },
        { key: 'value', label: 'Value', placeholder: '10', required: true },
        { key: 'expires', label: 'Expires (optional)', type: 'date' },
      ],
    },
  },
  {
    id: 'gift-cards', icon: Gift, title: 'Gift cards',
    desc: 'Sell digital gift cards customers can redeem at checkout.',
    setup: {
      title: 'Enable gift cards',
      description: 'Customers can purchase digital gift cards in the denominations you set.',
      fields: [
        { key: 'denominations', label: 'Denominations (comma-separated $)', placeholder: '25, 50, 100, 250', required: true },
        { key: 'expires_days', label: 'Expires after (days)', type: 'number', placeholder: '365' },
        { key: 'message', label: 'Card message', type: 'textarea', placeholder: 'A gift from us to you.' },
      ],
    },
  },
  {
    id: 'memberships', icon: Users, title: 'Memberships',
    desc: 'Offer exclusive content & perks behind a paywall.',
    setup: {
      title: 'Create a membership tier',
      fields: [
        { key: 'tier_name', label: 'Tier name', placeholder: 'Insider', required: true },
        { key: 'price', label: 'Monthly price ($)', type: 'number', placeholder: '9', required: true },
        { key: 'perks', label: 'Perks', type: 'textarea', placeholder: 'List the benefits of this tier' },
      ],
    },
  },
  {
    id: 'abandoned-cart', icon: ShoppingBag, title: 'Abandoned cart',
    desc: 'Automatically email customers who leave items behind.',
    setup: {
      title: 'Enable abandoned cart emails',
      fields: [
        { key: 'delay_hours', label: 'Send after (hours)', type: 'number', placeholder: '1', required: true },
        { key: 'subject', label: 'Email subject', placeholder: 'You left something behind!', required: true },
        { key: 'discount_code', label: 'Optional discount code', placeholder: 'COMEBACK10' },
      ],
    },
  },
  {
    id: 'notify-back', icon: Bell, title: 'Back-in-stock alerts',
    desc: 'Notify customers when their wishlisted items return.',
    setup: {
      title: 'Enable back-in-stock alerts',
      fields: [
        { key: 'subject', label: 'Email subject', placeholder: '{Product} is back!', required: true },
        { key: 'auto_enable', label: 'Auto-enable on all products?', type: 'select', options: ['Yes', 'No, opt-in per product'], required: true },
      ],
    },
  },
  {
    id: 'insights', icon: BarChart3, title: 'Conversion insights',
    desc: 'See where shoppers drop off and optimise the funnel.',
    setup: {
      title: 'Activate conversion tracking',
      description: 'We\'ll start recording funnel events on your site. No code needed.',
      fields: [
        { key: 'goal_url', label: 'Primary goal URL (e.g. /checkout/success)', required: true },
        { key: 'utm_track', label: 'Track UTM parameters?', type: 'select', options: ['Yes', 'No'] },
      ],
    },
  },
];

export default function DashboardSellingTools() {
  const [activeTool, setActiveTool] = useState(null);
  const token = localStorage.getItem('token');

  const handleSubmit = async (toolId, values) => {
    // Persist to /api/builder/selling-tools (no-op gracefully if endpoint missing)
    try {
      await axios.post(`${API}/builder/selling-tools/${toolId}`, values, {
        headers: { Authorization: `Bearer ${token}` },
      });
    } catch {
      // graceful fallback — store locally so user sees the value remembered
      const all = JSON.parse(localStorage.getItem('wb_selling_tools') || '{}');
      all[toolId] = { ...values, configured_at: new Date().toISOString() };
      localStorage.setItem('wb_selling_tools', JSON.stringify(all));
    }
    toast.success(`${activeTool.title} configured`);
  };

  return (
    <>
      <SqsPageHeader
        breadcrumbs="Products & Services"
        title="Selling Tools"
        subtitle="Conversion-boosting features for your storefront."
      />
      <div className="p-10 max-w-6xl grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
        {TOOLS.map(t => (
          <div key={t.id} className="bg-[#15152E] border border-white/[0.06] rounded-xl p-6 hover:border-white/[0.16] transition-all flex flex-col" data-testid={`tool-${t.id}`}>
            <div className="w-10 h-10 rounded-lg bg-[#A78BFA]/10 flex items-center justify-center mb-4">
              <t.icon size={18} className="text-[#A78BFA]" />
            </div>
            <h3 className="text-[14.5px] font-bold text-white mb-2">{t.title}</h3>
            <p className="text-[12px] text-[#9CA3AF] leading-relaxed mb-4 flex-1">{t.desc}</p>
            <button
              onClick={() => setActiveTool(t)}
              className="text-[10.5px] uppercase tracking-[0.18em] text-[#A78BFA] font-bold hover:text-white self-start"
              data-testid={`tool-configure-${t.id}`}
            >
              Configure →
            </button>
          </div>
        ))}
      </div>

      {activeTool && (
        <SetupModal
          open={!!activeTool}
          onClose={() => setActiveTool(null)}
          title={activeTool.setup.title}
          description={activeTool.setup.description}
          icon={activeTool.icon}
          fields={activeTool.setup.fields}
          onSubmit={(v) => handleSubmit(activeTool.id, v)}
        />
      )}
    </>
  );
}
